import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import BayilikBasvuruOtpModal from '../components/BayilikBasvuruOtpModal';
import BayilikBasvuruShell from '../components/BayilikBasvuruShell';
import BayilikSifreGereksinimleri from '../components/BayilikSifreGereksinimleri';
import {
  checkBayilikKullaniciAdi,
  fetchBayilikBasvuruForm,
  fetchBayilikBasvuruIlceler,
  submitBayilikBasvuru,
} from '../services/bayilikBasvuruService';
import { normalizeTurkishPhone } from '../utils/phoneNormalize';
import { isPasswordValid } from '../utils/bayilikBasvuruPassword';
import './LoginPage.css';
import './BayilikBasvuruPublicPage.css';

const KULLANICI_ADI_MIN = 6;

const EMPTY_FORM = {
  bayiAdi: '',
  yetkiliKisi: '',
  telefon: '',
  mail: '',
  kullaniciAdi: '',
  sifre: '',
  sifreTekrar: '',
  cityId: '',
  districtId: '',
  adres: '',
};

function formatPhoneMaskInput(value) {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.startsWith('0')) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return `0 (${digits}`;
  if (digits.length <= 6) return `0 (${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 8) {
    return `0 (${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return `0 (${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
}

function phoneDigitsForApi(maskedValue) {
  const digits = normalizeTurkishPhone(maskedValue);
  if (digits.length !== 10 || !/^5[0-9]{9}$/.test(digits)) return '';
  return digits;
}

function isValidEmail(value) {
  if (!value || !String(value).trim()) return true;
  return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(String(value).trim());
}

function DurumKarti({ durumKarti }) {
  if (!durumKarti) return null;
  return (
    <div className={`bayilik-durum-kart ${durumKarti.durum}`}>
      <div>{durumKarti.mesaj}</div>
      {durumKarti.redNedeni && <p>{durumKarti.redNedeni}</p>}
    </div>
  );
}

export default function BayilikBasvuruPublicPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [durumKarti, setDurumKarti] = useState(null);
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [telefonDogrulandi, setTelefonDogrulandi] = useState(false);
  const [telefonDogrulamaToken, setTelefonDogrulamaToken] = useState('');
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [kullaniciAdiMesaj, setKullaniciAdiMesaj] = useState('');
  const [kullaniciAdiOk, setKullaniciAdiOk] = useState(null);

  const telefonApi = useMemo(() => phoneDigitsForApi(form.telefon), [form.telefon]);

  const sifrelerUyusuyor = !form.sifreTekrar || form.sifre === form.sifreTekrar;
  const showSifreUyusmazlik = form.sifreTekrar.length > 0 && !sifrelerUyusuyor;

  const canSubmit = useMemo(() => {
    if (readOnly || submitLoading) return false;
    if (!form.bayiAdi.trim()) return false;
    if (!form.yetkiliKisi.trim()) return false;
    if (!telefonApi || !form.telefon.startsWith('0 (5')) return false;
    if (!telefonDogrulandi || !telefonDogrulamaToken) return false;
    if (!form.kullaniciAdi.trim() || form.kullaniciAdi.trim().length < KULLANICI_ADI_MIN) return false;
    if (kullaniciAdiOk !== true) return false;
    if (!isPasswordValid(form.sifre)) return false;
    if (!sifrelerUyusuyor) return false;
    if (!form.cityId || !form.districtId) return false;
    if (!form.adres.trim() || form.adres.trim().length < 5) return false;
    if (!isValidEmail(form.mail)) return false;
    return true;
  }, [
    readOnly,
    submitLoading,
    form,
    telefonApi,
    telefonDogrulandi,
    telefonDogrulamaToken,
    kullaniciAdiOk,
    sifrelerUyusuyor,
  ]);

  const loadForm = useCallback(async () => {
    if (!token) {
      setError('Geçersiz link');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetchBayilikBasvuruForm(token);
      const veri = res?.veri || {};
      setReadOnly(!!veri.readOnly);
      setDurumKarti(veri.durumKarti || null);
      setIller(veri.iller || []);
      setTelefonDogrulandi(!!veri.telefonDogrulandi);

      if (veri.basvuru) {
        const b = veri.basvuru;
        setForm({
          bayiAdi: b.bayiAdi || '',
          yetkiliKisi: b.yetkiliKisi || '',
          telefon: b.telefon ? formatPhoneMaskInput(b.telefon) : '',
          mail: b.mail || '',
          kullaniciAdi: b.kullaniciAdi || '',
          sifre: '',
          sifreTekrar: '',
          cityId: b.cityId ? String(b.cityId) : '',
          districtId: b.districtId ? String(b.districtId) : '',
          adres: b.adres || '',
        });
      }
    } catch (err) {
      setError(err?.message || 'Form yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    document.title = 'ÜNMAK | Bayi Sistem Kayıt Formu';
    loadForm();
    return () => { document.title = 'SM Yönetim Sistemi'; };
  }, [loadForm]);

  useEffect(() => {
    if (!token || !form.cityId || readOnly) {
      setIlceler([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchBayilikBasvuruIlceler(token, form.cityId);
        if (!cancelled) setIlceler(res?.veri?.ilceler || []);
      } catch {
        if (!cancelled) setIlceler([]);
      }
    })();
    return () => { cancelled = true; };
  }, [token, form.cityId, readOnly]);

  useEffect(() => {
    if (readOnly || !form.kullaniciAdi || form.kullaniciAdi.trim().length < KULLANICI_ADI_MIN) {
      setKullaniciAdiMesaj('');
      setKullaniciAdiOk(null);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await checkBayilikKullaniciAdi(token, form.kullaniciAdi.trim().toLowerCase());
        const musait = res?.veri?.musait;
        setKullaniciAdiOk(musait);
        setKullaniciAdiMesaj(musait ? '' : (res?.veri?.mesaj || 'Bu kullanıcı adı alınmış'));
      } catch (err) {
        setKullaniciAdiOk(false);
        setKullaniciAdiMesaj(err?.message || 'Kontrol yapılamadı');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.kullaniciAdi, token, readOnly]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'telefon') {
      setForm((prev) => ({ ...prev, telefon: formatPhoneMaskInput(value) }));
      setTelefonDogrulandi(false);
      setTelefonDogrulamaToken('');
      return;
    }
    if (name === 'cityId') {
      setForm((prev) => ({ ...prev, cityId: value, districtId: '' }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOtpSuccess = (veri) => {
    setTelefonDogrulandi(true);
    setTelefonDogrulamaToken(veri?.telefonDogrulamaToken || '');
    if (veri?.telefon) {
      setForm((prev) => ({ ...prev, telefon: formatPhoneMaskInput(veri.telefon) }));
    }
  };

  const validateForm = () => {
    if (!form.bayiAdi.trim()) return 'Bayi adı gereklidir';
    if (!form.yetkiliKisi.trim()) return 'Yetkili kişi gereklidir';
    if (!telefonApi || !form.telefon.startsWith('0 (5')) return 'Geçerli cep telefonu giriniz';
    if (!telefonDogrulandi || !telefonDogrulamaToken) return 'Telefon doğrulaması gereklidir';
    if (!form.kullaniciAdi.trim() || form.kullaniciAdi.trim().length < KULLANICI_ADI_MIN) {
      return 'Kullanıcı adı en az 6 karakter olmalı';
    }
    if (kullaniciAdiOk === false) return 'Bu kullanıcı adı alınmış';
    if (!isPasswordValid(form.sifre)) return 'Şifre gereksinimlerini karşılayın';
    if (!sifrelerUyusuyor) return 'Şifreler uyuşmuyor';
    if (!form.cityId) return 'İl seçimi gereklidir';
    if (!form.districtId) return 'İlçe seçimi gereklidir';
    if (!form.adres.trim() || form.adres.trim().length < 5) return 'Adres gereklidir';
    if (!isValidEmail(form.mail)) return 'Geçerli e-posta giriniz';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setSubmitLoading(true);
    try {
      const res = await submitBayilikBasvuru(token, {
        bayiAdi: form.bayiAdi.trim(),
        yetkiliKisi: form.yetkiliKisi.trim(),
        telefon: telefonApi,
        mail: form.mail.trim() || null,
        kullaniciAdi: form.kullaniciAdi.trim().toLowerCase(),
        sifre: form.sifre,
        sifreTekrar: form.sifreTekrar,
        adres: form.adres.trim(),
        cityId: form.cityId,
        districtId: form.districtId,
        telefonDogrulamaToken,
      });
      setReadOnly(true);
      setDurumKarti(res?.veri?.durumKarti || { durum: 'onay_bekliyor', mesaj: 'Onay bekliyor' });
      if (res?.veri?.basvuru) {
        const b = res.veri.basvuru;
        setForm((prev) => ({
          ...prev,
          bayiAdi: b.bayiAdi || prev.bayiAdi,
          yetkiliKisi: b.yetkiliKisi || prev.yetkiliKisi,
          telefon: b.telefon ? formatPhoneMaskInput(b.telefon) : prev.telefon,
          mail: b.mail || '',
          kullaniciAdi: b.kullaniciAdi || prev.kullaniciAdi,
          sifre: '',
          sifreTekrar: '',
          cityId: b.cityId ? String(b.cityId) : prev.cityId,
          districtId: b.districtId ? String(b.districtId) : prev.districtId,
          adres: b.adres || prev.adres,
        }));
      }
    } catch (err) {
      setFormError(err?.message || 'Başvuru gönderilemedi');
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <BayilikBasvuruShell>
        <div className="bayilik-basvuru-loading">
          <div className="loading-spinner" />
          <span>Yükleniyor...</span>
        </div>
      </BayilikBasvuruShell>
    );
  }

  if (error) {
    return (
      <BayilikBasvuruShell>
        <div className="bayilik-error-page">{error}</div>
      </BayilikBasvuruShell>
    );
  }

  return (
    <BayilikBasvuruShell>
      <DurumKarti durumKarti={durumKarti} />

      <form className="bayilik-form-grid" onSubmit={handleSubmit}>
          {formError && <div className="form-error">{formError}</div>}

          <div className="form-group">
            <label htmlFor="bayiAdi">Bayi Adı</label>
            <input
              id="bayiAdi"
              name="bayiAdi"
              value={form.bayiAdi}
              onChange={handleChange}
              disabled={readOnly}
              maxLength={200}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="yetkiliKisi">Bayi Yetkili Kişisi</label>
            <input
              id="yetkiliKisi"
              name="yetkiliKisi"
              value={form.yetkiliKisi}
              onChange={handleChange}
              disabled={readOnly}
              maxLength={100}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="telefon">Bayi Telefon</label>
            <div className="bayilik-telefon-row">
              <input
                id="telefon"
                name="telefon"
                value={form.telefon}
                onChange={handleChange}
                disabled={readOnly}
                inputMode="numeric"
                placeholder="0 (5XX) XXX XX XX"
                required
              />
              {!readOnly && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    if (!telefonApi) {
                      setFormError('Geçerli cep telefonu giriniz');
                      return;
                    }
                    setFormError('');
                    setOtpModalOpen(true);
                  }}
                  disabled={!telefonApi}
                >
                  Doğrula
                </button>
              )}
            </div>
            {telefonDogrulandi && (
              <div className="bayilik-verified-badge">
                <i className="fas fa-check-circle" aria-hidden="true" />
                Doğrulandı
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="kullaniciAdi">Kullanıcı Adı</label>
            <input
              id="kullaniciAdi"
              name="kullaniciAdi"
              value={form.kullaniciAdi}
              onChange={handleChange}
              disabled={readOnly}
              minLength={KULLANICI_ADI_MIN}
              maxLength={50}
              autoComplete="username"
              required
            />
            {kullaniciAdiMesaj && (
              <div className="bayilik-field-hint error">{kullaniciAdiMesaj}</div>
            )}
            {kullaniciAdiOk && !readOnly && (
              <div className="bayilik-field-hint ok">Kullanılabilir</div>
            )}
          </div>

          {!readOnly && (
            <div className="bayilik-form-row-2 bayilik-sifre-row">
              <div className="form-group">
                <label htmlFor="sifre">Şifre</label>
                <BayilikSifreGereksinimleri password={form.sifre} visible={!readOnly}>
                  <input
                    id="sifre"
                    name="sifre"
                    type="password"
                    value={form.sifre}
                    onChange={handleChange}
                    autoComplete="new-password"
                    maxLength={32}
                  />
                </BayilikSifreGereksinimleri>
              </div>
              <div className="form-group">
                <label htmlFor="sifreTekrar">Şifre Tekrar</label>
                <input
                  id="sifreTekrar"
                  name="sifreTekrar"
                  type="password"
                  value={form.sifreTekrar}
                  onChange={handleChange}
                  autoComplete="new-password"
                  maxLength={32}
                />
                {showSifreUyusmazlik && (
                  <div className="bayilik-sifre-mismatch">Şifreler uyuşmuyor</div>
                )}
              </div>
            </div>
          )}

          <div className="bayilik-form-row-2">
            <div className="form-group">
              <label htmlFor="cityId">İl</label>
              <select
                id="cityId"
                name="cityId"
                value={form.cityId}
                onChange={handleChange}
                disabled={readOnly}
                required
              >
                <option value="">Seçiniz</option>
                {iller.map((il) => (
                  <option key={il.id} value={il.id}>{il.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="districtId">İlçe</label>
              <select
                id="districtId"
                name="districtId"
                value={form.districtId}
                onChange={handleChange}
                disabled={readOnly || !form.cityId}
                required
              >
                <option value="">Seçiniz</option>
                {ilceler.map((ilce) => (
                  <option key={ilce.id} value={ilce.id}>{ilce.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="adres">Adres</label>
            <textarea
              id="adres"
              name="adres"
              value={form.adres}
              onChange={handleChange}
              disabled={readOnly}
              rows={3}
              maxLength={500}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="mail">E-posta</label>
            <input
              id="mail"
              name="mail"
              type="email"
              value={form.mail}
              onChange={handleChange}
              disabled={readOnly}
              autoComplete="email"
            />
          </div>

          {!readOnly && (
            <div className="bayilik-basvuru-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!canSubmit || submitLoading}
              >
                {submitLoading ? 'Başvuruluyor...' : 'Başvur'}
              </button>
            </div>
          )}
        </form>

      <BayilikBasvuruOtpModal
        isOpen={otpModalOpen}
        token={token}
        telefon={telefonApi}
        onSuccess={handleOtpSuccess}
        onClose={() => setOtpModalOpen(false)}
      />
    </BayilikBasvuruShell>
  );
}
